import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FileText,
  HelpCircle,
  Info,
  Mail,
  Plus,
  RefreshCw,
  Trash2
} from 'lucide-react'

import { Note } from '@components/note'
import { ConfirmModal, PdfActionMenuItems, pdfActionItems } from '@components/common'
import { useRevertedInvoice } from '@caradasstore/InvoiceEditStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import { useUserStore } from '@caradasstore/UserStore'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { BrandedModal, FormModalFooter } from '@components/branded-modal'
import { CarImageHolder } from '@components/car-image-holder'
import { CustomItem } from '@components/custom-item'
import { Drawers } from '@components/drawers'
import { AdasBox, AdasContainer, AdasImage, AdasTextField, AdasTypography } from '@components/wrapper-components'
import {
  convertToStandardWO,
  convertWorkOrderToInvoice,
  deleteWorkOrder,
  getCalibrationList,
  getQWIByWorkOrderId,
  getServiceList,
  getWorkOrderByReferenceNumber,
  getWorkorderEmailPreview,
  getWorkorderPdf,
  updateNote,
  updateWorkOrderCalibrations,
  updateWorkOrderServices,
  updateClient,
  getPartsList,
  getLabourList,
  updateWorkOrderParts,
  updateWorkOrderLabour,
  getReferralByCode
} from '../../../api/api'
import { Logger } from '../../../logger'
import { handlePdf, PDF_ACTION } from '../../../utils/pdfActions'
import { useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter } from '../../../utils/utils'
import {
  GENERIC_MESSAGES,
  BUTTONS_DISPLAY_LABEL,
  PARAM_NAMES,
  REDIRECT_URLS,
  getInvoiceSummaryUrl,
  getUrlByNameAndId
} from '../../../constants'
import { StatusEnum } from '../../../enums/enums'
import { EmailForm } from '@components/email'
import ExpandableSection from '@components/custom-item/item-list/ExpandableSection'
import { MobileSection } from '@components/custom-item/item-list/MobileSection'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { EditProductPricing } from '@components/product/edit-product-pricing/EditProductPricing'
import { useEditPricing } from '../../../hooks/useEditPricing'
import { flattenItem, isNationalAgreementLine, normalizeCustomItems, splitCustomItems } from '../../../utils/itemHelpers'
import { handleDrawerClose } from '../../../hooks/handleDrawerClose'
import { MAX_DESCRIPTION_LENGTH } from '../../../constants'
import { itemHandlersFactory } from 'utils/populateItemsUtil'
import { ProductContractBanner, hasContractBanner } from '@components/product'
import { CalibrationLevelLegend, shouldShowLevelLegend } from '@components/calibration-level-legend'
import { CreateSupportRequestModal } from '@pages/support/support-request-modal'
import { useIsReadOnly } from '@hooks/useIsReadOnly'

const CalibrationStepRow = ({ label, complete, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F8FAFC]'
  >
    <AdasBox className='flex items-center gap-3'>
      <span
        className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full ${
          complete ? 'bg-[#16A34A] text-white' : 'border border-[#D1D5D8] bg-white text-[#6B7280]'
        }`}
      >
        {complete && <CheckCircle2 className='h-4 w-4' />}
      </span>
      <span className='text-[14px] font-semibold text-[#0A1628]'>{label}</span>
      <span className={`text-[12px] font-semibold uppercase tracking-wide ${complete ? 'text-[#16A34A]' : 'text-[#6B7280]'}`}>
        {complete ? 'Complete' : 'Incomplete'}
      </span>
    </AdasBox>
    <ChevronRight className='h-4 w-4 text-[#6B7280]' />
  </button>
)

export const WorkOrderSummaryPage = () => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const [packages, setPackages] = useState([])
  const [services, setServices] = useState([])
  const [parts, setParts] = useState([])
  const [labor, setLabor] = useState([])
  const [availableCalibrations, setAvailableCalibrations] = useState()
  const [availableServices, setAvailableServices] = useState()
  const [availableParts, setAvailableParts] = useState()
  const [availableLabour, setAvailableLabour] = useState()

  const [supportOpen, setSupportOpen] = useState(false)
  const [isCalibrationOpen, setIsCalibrationOpen] = useState(false)
  const [isServiceOpen, setIsServiceOpen] = useState(false)
  const [isPartsOpen, setIsPartsOpen] = useState(false)
  const [isLabourOpen, setIsLabourOpen] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [customItemList, setCustomItemList] = useState([])
  const [rebateCustomItemList, setRebateCustomItemList] = useState([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const currentUser = useUserStore((store) => store.currentUser)

  const location = useLocation()
  const isAssignedToMe = location?.state?.isAssignedToMe
  const dataFromInvoiceScreen = location?.state?.dataFromInvoiceScreen
  const [isEmail, setIsEmail] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [workorderPdf, setWorkorderPdf] = useState()
  const [workOrderData, setWorkOrderData] = useState({})
  const [QWIData, setQWIData] = useState()
  const setLoading = useLoadingStore((store) => store.setLoading)
  const isLoading = useLoadingStore((store) => store.loading)
  const addMessage = useMessageStore((store) => store.addMessage)
  const [notes, setNotes] = useState({
    notes_external: '',
    notes_internal: '',
    client_notes: ''
  })
  const [isServiceOnly, setIsServiceOnly] = useState(false)
  const [referralData, setReferralData] = useState()
  const [open, setOpen] = useState(false)

  const history = useHistory()
  const setRevertedInvoice = useRevertedInvoice((store) => store.setRevertedInvoice)

  // Actions dropdown
  const [actionsOpen, setActionsOpen] = useState(false)
  const actionsRef = useRef(null)
  useEffect(() => {
    if (!actionsOpen) return
    const handler = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [actionsOpen])

  const redirectToForbiddenPage = () => history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
  const redirectPreCalibration = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_PRE_CALIBRATION, referenceNumber))
  const redirectCalibration = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_CALIBRATION, referenceNumber))
  const redirectPostCalibration = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_POST_CALIBRATION, referenceNumber))
  const redirectVehicleInfo = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_VEHICLE_INFO, referenceNumber))
  const redirectDocuments = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_DOCUMENT, referenceNumber))
  const redirectPhotos = () => history.push(getUrlByNameAndId(REDIRECT_URLS.WORK_ORDER_PHOTO, referenceNumber))

  const saveNote = useCallback(
    async (notesPayload) => {
      const isSuccess = (status) => status === 200 || status === 201
      if (notesPayload.noteType === 'client_note') {
        const clientNotes = notesPayload?.note?.client_notes
        const updatedClientData = { ...workOrderData.client, notes: clientNotes }
        const { status, data } = await updateClient({ data: updatedClientData })
        if (isSuccess(status)) setNotes((prev) => ({ ...prev, client_notes: data.notes }))
      } else {
        const { status, data } = await updateNote({ type: 'workorders', id: workOrderData.id, data: notesPayload })
        if (isSuccess(status)) {
          const updatedWorkOrder = data.workOrder
          setNotes((prev) => ({
            ...prev,
            notes_external: updatedWorkOrder.notes_external,
            notes_internal: updatedWorkOrder.notes_internal,
            client_notes: workOrderData.client?.notes
          }))
        }
      }
    },
    [workOrderData.id, workOrderData.client]
  )

  const calculateTotal = (list = []) => list.reduce((sum, { total_price = 0 }) => sum + total_price, 0)
  const packagesTotal = useMemo(() => calculateTotal(packages), [packages])
  const servicesTotal = useMemo(() => calculateTotal(services), [services])
  const partsTotal = useMemo(() => calculateTotal(parts), [parts])
  const laborTotal = useMemo(() => calculateTotal(labor), [labor])

  const getCustomItemTotal = useCallback(
    (items = []) =>
      items.length > 0
        ? items.reduce((tp, current) => (current.type === 'DISCOUNT' ? tp - current.total_price : tp + current.total_price), 0)
        : 0,
    []
  )
  const customItemTotal = useMemo(() => getCustomItemTotal(customItemList), [customItemList, getCustomItemTotal])
  // CAR-3893: the client rebate is only shown on invoices; a work order totals to the figure before it.
  const totalCost = useMemo(
    () => packagesTotal + servicesTotal + partsTotal + laborTotal + customItemTotal,
    [packagesTotal, servicesTotal, partsTotal, laborTotal, customItemTotal]
  )

  const handleConfirm = async () => {
    try {
      await deleteWorkOrder({ workOrderId: workOrderData.id })
      setRevertedInvoice(undefined)
      history.push('/')
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'WorkOrderSummaryPage/handleConfirm', workOrderId: workOrderData.id } })
    }
  }
  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setDeleteInput('')
  }

  const handleAddToInvoice = async (shouldUpdateInvoice) => {
    try {
      const createInvoiceResponse = await convertWorkOrderToInvoice({ workOrderId: workOrderData.id })
      if (createInvoiceResponse.status === 200) {
        const redirectPath = shouldUpdateInvoice ? getUrlByNameAndId(REDIRECT_URLS.INVOICE_SUMMARY, referenceNumber) : '/invoices'
        shouldUpdateInvoice ? history.push(redirectPath) : history.replace(redirectPath)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'WorkOrderSummaryPage/handleAddToInvoice', workOrderId: workOrderData.id } })
    }
  }

  const convertServiceOnlyToStandardWo = useCallback(async () => {
    try {
      setShowConfirmModal(false)
      await convertToStandardWO(workOrderData.id)
      setIsServiceOnly(false)
    } catch (error) {
      Logger.error({
        message: error,
        payload: { file: 'WorkOrderSummaryPage/handleConvertToStandardWo', workOrderId: workOrderData.id }
      })
    }
  }, [workOrderData.id])

  const ConvertToStandardWoWithConfirmation = () => setShowConfirmModal(true)

  const onCloseCalibrations = (data) =>
    handleDrawerClose({
      setLoading,
      setDrawerOpen: setIsCalibrationOpen,
      updateFn: updateWorkOrderCalibrations,
      setItems: setPackages,
      sourceData: workOrderData,
      data,
      logFile: 'updateWorkOrderCalibrations',
      itemKey: 'calibrations',
      type: 'workorders',
      onSuccess: fetchData
    })

  const onCloseServices = (data) =>
    handleDrawerClose({
      setLoading,
      setDrawerOpen: setIsServiceOpen,
      updateFn: updateWorkOrderServices,
      setItems: setServices,
      sourceData: workOrderData,
      data,
      logFile: 'updateWorkOrderServices',
      itemKey: 'services',
      type: 'workorders',
      onSuccess: fetchData
    })

  const onCloseParts = (data) =>
    handleDrawerClose({
      setLoading,
      setDrawerOpen: setIsPartsOpen,
      updateFn: updateWorkOrderParts,
      setItems: setParts,
      sourceData: workOrderData,
      data,
      logFile: 'updateWorkOrderParts',
      itemKey: 'parts',
      type: 'workorders',
      onSuccess: fetchData
    })

  const onCloseLabour = (data) =>
    handleDrawerClose({
      setLoading,
      setDrawerOpen: setIsLabourOpen,
      updateFn: updateWorkOrderLabour,
      setItems: setLabor,
      sourceData: workOrderData,
      data,
      logFile: 'updateWorkOrderLabour',
      itemKey: 'labors',
      type: 'workorders',
      onSuccess: fetchData
    })

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await getWorkOrderByReferenceNumber({ referenceNumber })
      if (response?.status !== 200) {
        redirectToForbiddenPage()
        return
      }
      const { data } = response
      const { workOrder } = data
      // Converted to an invoice: the work order lives on as that invoice (same reference number). Open it
      // instead of the Forbidden page - an old link, bookmark or the list must not dead-end there.
      if (StatusEnum[workOrder?.status] === StatusEnum.MOVED) {
        history.replace(getInvoiceSummaryUrl(workOrder.reference_number || referenceNumber))
        return
      }
      Object.assign(workOrder, {
        safetySystemsItems: data.safetySystemsItems,
        calibrations: data.qwiCalibrations,
        services: data.qwiServices,
        parts: data.qwiParts,
        labors: data.qwiLabours,
        customItems: data.qwiCustomItems
      })
      setIsServiceOnly(workOrder.type === 'SERVICE_ONLY')
      setWorkOrderData(workOrder)
      const qwi_response = await getQWIByWorkOrderId(workOrder?.id)
      setQWIData(qwi_response.data)
      setNotes({
        notes_external: workOrder.notes_external || '',
        notes_internal: workOrder.notes_internal || '',
        client_notes: workOrder.client?.notes || ''
      })

      if (data.qwiCalibrations?.length) setPackages(data.qwiCalibrations.map((it) => flattenItem(it, 'calibration')))
      else setPackages([])
      if (data.qwiServices?.length) setServices(data.qwiServices.map((it) => flattenItem(it, 'service')))
      else setServices([])
      if (data.qwiParts?.length) setParts(data.qwiParts.map((it) => flattenItem(it, 'part')))
      else setParts([])
      if (data.qwiLabours?.length) setLabor(data.qwiLabours.map((it) => flattenItem(it, 'labour')))
      else setLabor([])

      if (data.qwiCustomItems?.length) {
        const updatedList = normalizeCustomItems(data.qwiCustomItems)
        const { rebateItems, standardItems } = splitCustomItems(updatedList)
        setCustomItemList(standardItems)
        setRebateCustomItemList(rebateItems)
      } else {
        setCustomItemList([])
        setRebateCustomItemList([])
      }

      if (dataFromInvoiceScreen) setRevertedInvoice(workOrder)
    } catch (error) {
      Logger.error({
        message: error,
        payload: { referenceNumber: workOrderData?.reference_number, ccid: workOrderData?.calibrationCenter?.id }
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (referenceNumber) fetchData()
  }, [referenceNumber, isServiceOnly])

  useEffect(() => {
    setWorkOrderData((prev) => ({ ...prev, customItems: [...customItemList, ...rebateCustomItemList] }))
  }, [customItemList, rebateCustomItemList])

  const {
    selectedProduct,
    setSelectedProduct,
    isOpenEditProduct,
    setIsOpenEditProduct,
    handleClickEditPricing,
    handleEditPricingInputChange,
    handleEditPricingSubmit
  } = useEditPricing(fetchData)

  const vehicleData = workOrderData?.vehicle || {}
  const isClosed = StatusEnum[workOrderData?.status] === StatusEnum.CLOSED
  // A client user gets the same treatment as a closed work order: nothing is editable.
  const isReadOnly = useIsReadOnly()
  const isLocked = isClosed || isReadOnly
  // Client users (read-only) view and download a work order; they never delete, email, invoice
  // or raise support on it, so those actions are not offered at all.
  const showDeleteButton = useMemo(
    () => !isReadOnly && (!dataFromInvoiceScreen || (dataFromInvoiceScreen && (currentUser?.is_admin || currentUser?.is_owner))),
    [dataFromInvoiceScreen, currentUser, isReadOnly]
  )
  const deleteDisabled = !currentUser?.is_admin && !currentUser?.is_owner && QWIData && QWIData.invoiceId
  const calibrationStepsComplete =
    workOrderData.is_precalibration_complete && workOrderData.is_postcalibration_complete && workOrderData.is_calibration_step_complete

  const isServiceOnlyForType = workOrderData?.type === 'SERVICE_ONLY'
  // National Agreement work orders carry a referral source code — their line items are
  // read-only for every role (view-only sheet, view icon instead of the edit pencil).
  const isNationalAgreement = !!QWIData?.referralSourceCode

  const emailWorkorderHandler = useCallback(async () => {
    try {
      const emailContactResponse = await getWorkorderEmailPreview(workOrderData?.id)
      if (emailContactResponse.status === 200) {
        setIsEmail(true)
        setEmailContacts(emailContactResponse.data)
      }
      const workorderPdfResponse = await getWorkorderPdf(workOrderData?.id)
      if (workorderPdfResponse.status === 200) {
        const fileValue = new File([workorderPdfResponse.data], `workorder_${workOrderData?.reference_number}.pdf`)
        fileValue.id = workOrderData?.id
        setWorkorderPdf(fileValue)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { WorkorderId: workOrderData?.id, path: 'WorkOrderSummaryPage/emailWorkorderHandler' } })
    }
  }, [workOrderData])

  // View, print or download the work order PDF (all users, client users included).
  const workorderPdfHandler = useCallback(
    async (action = PDF_ACTION.VIEW) => {
      if (!workOrderData?.id) return
      try {
        setLoading(true)
        const workorderPdfResponse = await getWorkorderPdf(workOrderData.id)
        if (workorderPdfResponse.status === 200) {
          handlePdf(workorderPdfResponse.data, `workorder_${workOrderData.reference_number}.pdf`, action)
        }
      } catch (error) {
        Logger.error({
          message: error,
          payload: { WorkorderId: workOrderData.id, action, path: 'WorkOrderSummaryPage/workorderPdfHandler' }
        })
      } finally {
        setLoading(false)
      }
    },
    [workOrderData]
  )

  // Register the Actions menu with the global mobile header so it replaces the
  // search/filter icons on this page. Items mirror the desktop dropdown.
  const setPageAction = useMobileToolbarStore((s) => s.setPageAction)
  const clearPageAction = useMobileToolbarStore((s) => s.clearPageAction)
  useEffect(() => {
    // Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, Delete, Support.
    const items = [
      ...(isReadOnly ? [] : [{ label: 'Email WO', icon: Mail, onClick: () => emailWorkorderHandler() }]),
      ...pdfActionItems('WO', workorderPdfHandler),
      ...(showDeleteButton ? [{ label: 'Delete WO', icon: Trash2, onClick: () => !deleteDisabled && handleOpen() }] : []),
      ...(isReadOnly ? [] : [{ label: 'Support', icon: HelpCircle, onClick: () => setSupportOpen(true) }])
    ]
    setPageAction({ label: 'Actions', items })
    return () => clearPageAction()
  }, [
    showDeleteButton,
    deleteDisabled,
    isReadOnly,
    emailWorkorderHandler,
    workorderPdfHandler,
    history,
    referenceNumber,
    setPageAction,
    clearPageAction
  ])

  const handleClickClosePricing = (type) => {
    setIsOpenEditProduct((prev) => ({ ...prev, [type]: false }))
    setSelectedProduct({})
  }

  const onSaveCustomItem = (items) => {
    const normalizedItems = normalizeCustomItems(items)
    const { rebateItems, standardItems } = splitCustomItems(normalizedItems)
    setCustomItemList(standardItems)
    setRebateCustomItemList(rebateItems)
    fetchData()
  }

  const { populateSelectableItems } = itemHandlersFactory({
    getCalibrationList,
    getServiceList,
    getPartsList,
    getLabourList,
    setAvailableCalibrations,
    setAvailableServices,
    setAvailableParts,
    setAvailableLabour
  })
  const drawerSetters = {
    calibrations: setIsCalibrationOpen,
    services: setIsServiceOpen,
    parts: setIsPartsOpen,
    labor: setIsLabourOpen
  }

  const drawerTitles = { calibrations: 'Calibrations', services: 'Services', parts: 'Parts', labor: 'Labor' }

  // National-agreement work orders must use the referral catalog. If the referral hasn't loaded yet
  // (the effect below is still in flight), load it now rather than fall back to the centre catalog.
  const resolveReferral = async () => {
    if (referralData?.id || !QWIData?.referralSourceCode) return referralData
    const response = await getReferralByCode({
      ccId: workOrderData?.calibrationCenter?.id,
      code: QWIData.referralSourceCode
    })
    if (response?.data?.id) setReferralData(response.data)
    return response?.data
  }

  const handleManageClick = async (type) => {
    const ccid = workOrderData?.calibrationCenter?.id
    const qwi_id = QWIData?.id
    const referral = type !== 'parts' ? await resolveReferral() : referralData
    const showLoadError = () => addMessage({ text: `Couldn't load the ${drawerTitles[type]} list. Nothing was changed, please try again.` })
    if (type !== 'parts' && QWIData?.referralSourceCode && !referral?.id) {
      showLoadError()
      return
    }
    // Tick the lines the close handler compares against (`workOrderData[itemKey]`), so an unchanged
    // drawer always closes with the same product ids and never saves.
    const selected = {
      calibrations: (workOrderData?.calibrations || []).map((it) => flattenItem(it, 'calibration')),
      services: (workOrderData?.services || []).map((it) => flattenItem(it, 'service')),
      parts: (workOrderData?.parts || []).map((it) => flattenItem(it, 'part')),
      labours: (workOrderData?.labors || []).map((it) => flattenItem(it, 'labour'))
    }
    let extraParams = {}
    if (type !== 'parts' && referral?.id) {
      extraParams = { belongsTo: 'REFERRAL', belongsToId: referral.id }
    }
    // Never open the drawer on a failed catalog load: closing it would save a list built from nothing.
    const loaded = await populateSelectableItems(type, ccid, qwi_id, selected, extraParams)
    if (!loaded) {
      showLoadError()
      return
    }
    drawerSetters[type]?.(true)
  }

  useEffect(() => {
    if (QWIData?.referralSourceCode) {
      const fetchReferralData = async () => {
        const referral_response = await getReferralByCode({
          ccId: workOrderData?.calibrationCenter?.id,
          code: QWIData?.referralSourceCode
        })
        if (referral_response?.data) setReferralData(referral_response.data)
      }
      fetchReferralData()
    }
  }, [QWIData?.referralSourceCode, workOrderData?.calibrationCenter?.id])

  return (
    <AdasContainer maxWidth={false}>
      {workOrderData.id && (
        <AdasBox className='mb-[60px] mt-5'>
          <EmailForm
            emailType='workorder'
            workOrderPdf={workorderPdf}
            isOpen={isEmail}
            contacts={emailContacts}
            emailData={workOrderData}
            onClose={() => setIsEmail(false)}
          />
          <Drawers
            data={availableCalibrations}
            isOpen={isCalibrationOpen}
            onClose={onCloseCalibrations}
            title={'Calibrations'}
            enableProductClassTabs={!referralData?.id}
          />
          <Drawers
            data={availableServices}
            isOpen={isServiceOpen}
            onClose={onCloseServices}
            title={'Service'}
            enableProductClassTabs={!referralData?.id}
          />
          <Drawers data={availableParts} isOpen={isPartsOpen} onClose={onCloseParts} title={'Parts'} />
          <Drawers data={availableLabour} isOpen={isLabourOpen} onClose={onCloseLabour} title={'Labor'} />

          {/* Mobile vehicle card — meta + type badge inside card, image, then title/total/client */}
          <AdasBox className='mb-3 min-[435px]:hidden rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='mb-3 flex items-center justify-between gap-3'>
              <AdasTypography className='text-[12px] text-[#2C3D4D]'>
                <span>#{workOrderData.reference_number}</span>
                {workOrderData.repair_order_num && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>RO {workOrderData.repair_order_num}</span>
                  </>
                )}
              </AdasTypography>
              <span
                className={`rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                  isServiceOnlyForType ? 'bg-[#FEF3C7] text-[#B45309]' : 'bg-[#DBEAFE] text-[#1F6FEB]'
                }`}
              >
                {isServiceOnlyForType ? 'Service-Only' : 'Standard'}
              </span>
            </AdasBox>
            <AdasBox className='flex h-[140px] items-center justify-center overflow-hidden rounded-md bg-white text-[#6B7280]'>
              {vehicleData?.image_url ? (
                <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-contain' />
              ) : (
                <CarImageHolder showText={false} />
              )}
            </AdasBox>
            <AdasBox className='mt-3 text-center'>
              <AdasTypography className='text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                {vehicleData?.year
                  ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                  : 'Vehicle'}
              </AdasTypography>
              <AdasTypography className='mt-1 text-[14px] font-semibold text-[#0A1628]'>
                Total: {currencyFormatter(totalCost)}
              </AdasTypography>
              <AdasTypography className='mt-0.5 text-[12px] text-[#2C3D4D]'>
                {workOrderData.client?.name && <span>{workOrderData.client.name}</span>}
                {workOrderData.insurance_company?.name && (
                  <>
                    <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                    <span>{workOrderData.insurance_company.name}</span>
                  </>
                )}
              </AdasTypography>
              {workOrderData.created_at && (
                <AdasTypography className='mt-1 text-[11px] text-[#2C3D4D]'>
                  Created{' '}
                  {(() => {
                    const d = new Date(workOrderData.created_at)
                    const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                    return `${date} ${time}`
                  })()}
                </AdasTypography>
              )}
              <AdasBox className='mt-2 flex justify-center' onClick={(e) => e.stopPropagation()}>
                <AssigneeAvatarPicker
                  type='workorders'
                  workOrderId={workOrderData.id}
                  initialAssignee={workOrderData.assigned_to}
                  closedStatus={isLocked}
                  showName
                  showEditIcon
                />
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {/* Page heading — Back link + vehicle title + assignee + Actions menu */}
          <AdasBox className='mb-4 hidden min-[435px]:flex flex-wrap items-end justify-between gap-4'>
            <AdasBox>
              <button
                type='button'
                onClick={() => history.goBack()}
                className='mb-1 text-[13px] font-medium text-[#1F6FEB] hover:underline'
              >
                Back to Work Orders
              </button>
              <h1 className='text-[28px] font-bold leading-tight text-[#0A1628]'>Work Order Summary</h1>
            </AdasBox>
            <AdasBox className='flex items-center gap-3'>
              <AssigneeAvatarPicker
                type='workorders'
                workOrderId={workOrderData.id}
                initialAssignee={workOrderData.assigned_to}
                closedStatus={isLocked}
                showName
                label='Assignee:'
              />
              <AdasBox ref={actionsRef} className='relative'>
                <button
                  type='button'
                  onClick={() => setActionsOpen((o) => !o)}
                  className='inline-flex items-center gap-2 rounded-lg bg-[#0A1628] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D]'
                >
                  Actions
                  {actionsOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
                </button>
                {actionsOpen && (
                  <AdasBox className='absolute right-0 top-full z-30 mt-2 w-[240px] overflow-hidden rounded-lg border border-[#EBEDF0] bg-white py-2 shadow-lg'>
                    {/* Shared order on Quote, WO and Invoice (CAR-4070): Email, View / Print / Download, Delete, Support. */}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          emailWorkorderHandler()
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-foreground transition-colors hover:bg-[#F8FAFC]'
                      >
                        <Mail className='h-4 w-4 text-[#475569]' />
                        Email WO
                      </button>
                    )}
                    <PdfActionMenuItems noun='WO' onAction={workorderPdfHandler} onClose={() => setActionsOpen(false)} />
                    {showDeleteButton && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          handleOpen()
                        }}
                        disabled={deleteDisabled}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-destructive transition-colors hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
                      >
                        <Trash2 className='h-4 w-4' />
                        Delete WO
                      </button>
                    )}
                    {!isReadOnly && (
                      <button
                        type='button'
                        onClick={() => {
                          setActionsOpen(false)
                          setSupportOpen(true)
                        }}
                        className='flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] text-foreground transition-colors hover:bg-[#F8FAFC]'
                      >
                        <HelpCircle className='h-4 w-4 text-[#475569]' />
                        Support
                      </button>
                    )}
                  </AdasBox>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {/* Vehicle summary card — thumbnail · title/VIN/client+RO# · WO# + date */}
          <AdasBox className='mb-4 hidden min-[435px]:block rounded-xl border border-[#D1D5D8] bg-white p-4'>
            <AdasBox className='flex items-stretch justify-between gap-4'>
              <AdasBox className='flex items-start gap-4'>
                <AdasBox className='flex h-[100px] w-[160px] flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-[#EBEDF0] text-[#6B7280]'>
                  {vehicleData?.image_url ? (
                    <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-cover' />
                  ) : (
                    <CarImageHolder showText={false} />
                  )}
                </AdasBox>
                <AdasBox className='min-w-0 flex-1'>
                  <AdasTypography className='text-[18px] font-bold leading-tight text-[#0A1628]'>
                    {vehicleData?.year
                      ? `${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim || ''}`.trim()
                      : 'Vehicle'}
                  </AdasTypography>
                  {vehicleData?.vin && (
                    <AdasTypography className='mt-2 font-mono text-[13px] tracking-wider text-[#2C3D4D]'>{vehicleData.vin}</AdasTypography>
                  )}
                  <AdasBox className='mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#2C3D4D]'>
                    {workOrderData.client?.name && <span>{workOrderData.client.name}</span>}
                    {workOrderData.insurance_company?.name && (
                      <>
                        <span className='text-[#D1D5D8]'>·</span>
                        <span>{workOrderData.insurance_company.name}</span>
                      </>
                    )}
                  </AdasBox>
                </AdasBox>
              </AdasBox>
              <AdasBox className='flex flex-col items-end whitespace-nowrap'>
                <AdasTypography className='text-[14px] leading-tight text-[#2C3D4D]'>
                  <span>WO# {workOrderData.reference_number}</span>
                  {workOrderData.repair_order_num && (
                    <>
                      <span className='mx-2 text-[#CBD5E1]'>|</span>
                      <span>RO# {workOrderData.repair_order_num}</span>
                    </>
                  )}
                </AdasTypography>
                {/* Spacer matches the VIN row height on the left so the date lines up with the client row */}
                <AdasBox className='mt-2 h-[18px]' aria-hidden='true' />
                {workOrderData.created_at && (
                  <AdasTypography className='mt-2 flex h-7 items-center text-[13px] text-[#2C3D4D]'>
                    {(() => {
                      const d = new Date(workOrderData.created_at)
                      const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                      return `${date} ${time}`
                    })()}
                  </AdasTypography>
                )}
              </AdasBox>
            </AdasBox>
          </AdasBox>

          {!isLoading && (
            <AdasBox>
              {QWIData?.referralSourceCode && <ProductContractBanner referralData={referralData} />}
              {/* Static key for the Level 1 / 2 / 3A / 3B / 3C codes in calibration names, styled like the
                  NA banner above. Shown only when the record has an Auto Quote line and that banner isn't shown (CAR-4019). */}
              {shouldShowLevelLegend({
                packages,
                services,
                isServiceOnly,
                hasContract: hasContractBanner(referralData ?? { code: QWIData?.referralSourceCode })
              }) && <CalibrationLevelLegend />}

              {/* Calibration workflow steps (WO-specific, not on Quotes) */}
              {!isServiceOnly && (
                <AdasBox className='mb-3 overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
                  <AdasBox className='flex items-center justify-between bg-[#E8EFFF] px-4 py-2.5'>
                    <span className='text-[14px] font-semibold text-[#0A1628]'>Calibration Workflow</span>
                    <span className='text-[12px] font-semibold uppercase tracking-wide text-[#475569]'>
                      {
                        [
                          workOrderData.is_precalibration_complete,
                          workOrderData.is_calibration_step_complete,
                          workOrderData.is_postcalibration_complete
                        ].filter(Boolean).length
                      }{' '}
                      / 3 complete
                    </span>
                  </AdasBox>
                  <AdasBox className='divide-y divide-[#EBEDF0]'>
                    <CalibrationStepRow
                      label='Pre Calibration Set Up'
                      complete={!!workOrderData.is_precalibration_complete}
                      onClick={redirectPreCalibration}
                    />
                    <CalibrationStepRow
                      label='Calibration Documentation'
                      complete={!!workOrderData.is_calibration_step_complete}
                      onClick={redirectCalibration}
                    />
                    <CalibrationStepRow
                      label='Post Calibration Checklist'
                      complete={!!workOrderData.is_postcalibration_complete}
                      onClick={redirectPostCalibration}
                    />
                  </AdasBox>
                </AdasBox>
              )}

              {/* Line item sections */}
              {!isServiceOnly && (
                <>
                  <div className='hidden min-[435px]:block'>
                    <ExpandableSection
                      title='Calibrations'
                      total={packagesTotal}
                      items={packages}
                      manageLabel='MANAGE CALIBRATIONS'
                      onManage={() => handleManageClick('calibrations')}
                      type={'calibration'}
                      onEdit={handleClickEditPricing}
                      isNational={isNationalAgreement}
                    />
                  </div>
                  <div className='min-[435px]:hidden'>
                    <MobileSection
                      title='Calibrations'
                      total={packagesTotal}
                      items={packages}
                      manageLabel='MANAGE CALIBRATIONS'
                      onManage={() => handleManageClick('calibrations')}
                      type='calibration'
                      onEdit={handleClickEditPricing}
                      isNational={isNationalAgreement}
                    />
                  </div>
                  <EditProductPricing
                    key='edit calibration'
                    type='calibration'
                    title='Calibrations'
                    product={selectedProduct}
                    descriptionLength={MAX_DESCRIPTION_LENGTH}
                    isOpen={isOpenEditProduct.calibration}
                    onClose={handleClickClosePricing}
                    handleInputChange={handleEditPricingInputChange}
                    onSubmit={handleEditPricingSubmit}
                    isNational={QWIData?.referralSourceCode}
                  />
                </>
              )}
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  title='Services'
                  total={servicesTotal}
                  items={services}
                  manageLabel='MANAGE SERVICES'
                  onManage={() => handleManageClick('services')}
                  type={'service'}
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Services'
                  total={servicesTotal}
                  items={services}
                  manageLabel='MANAGE SERVICES'
                  onManage={() => handleManageClick('services')}
                  type='service'
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <EditProductPricing
                key='edit service'
                type='service'
                title='Services'
                product={selectedProduct}
                descriptionLength={MAX_DESCRIPTION_LENGTH}
                isOpen={isOpenEditProduct.service}
                onClose={handleClickClosePricing}
                handleInputChange={handleEditPricingInputChange}
                onSubmit={handleEditPricingSubmit}
                isNational={QWIData?.referralSourceCode}
              />
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  key='Parts'
                  title='Parts'
                  total={partsTotal}
                  items={parts}
                  manageLabel='MANAGE PARTS'
                  onManage={() => handleManageClick('parts')}
                  type={'part'}
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Parts'
                  total={partsTotal}
                  items={parts}
                  manageLabel='MANAGE PARTS'
                  onManage={() => handleManageClick('parts')}
                  defaultExpanded={false}
                  type='part'
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <EditProductPricing
                key='edit part'
                type='part'
                title='Parts'
                product={selectedProduct}
                descriptionLength={MAX_DESCRIPTION_LENGTH}
                isOpen={isOpenEditProduct.part}
                onClose={handleClickClosePricing}
                handleInputChange={handleEditPricingInputChange}
                onSubmit={handleEditPricingSubmit}
                isNational={isNationalAgreement && isNationalAgreementLine(selectedProduct, 'part')}
              />
              <div className='hidden min-[435px]:block'>
                <ExpandableSection
                  key='Labour'
                  title='Labor'
                  total={laborTotal}
                  items={labor}
                  manageLabel='MANAGE LABOR'
                  onManage={() => handleManageClick('labor')}
                  type={'labor'}
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <div className='min-[435px]:hidden'>
                <MobileSection
                  title='Labor'
                  total={laborTotal}
                  items={labor}
                  manageLabel='MANAGE LABOR'
                  onManage={() => handleManageClick('labor')}
                  defaultExpanded={false}
                  type='labor'
                  onEdit={handleClickEditPricing}
                  isNational={isNationalAgreement}
                />
              </div>
              <EditProductPricing
                key='edit labor'
                type='labor'
                title='Labor'
                product={selectedProduct}
                descriptionLength={MAX_DESCRIPTION_LENGTH}
                isOpen={isOpenEditProduct.labor}
                onClose={handleClickClosePricing}
                handleInputChange={handleEditPricingInputChange}
                onSubmit={handleEditPricingSubmit}
                isNational={QWIData?.referralSourceCode}
              />
              <CustomItem
                type='workorders'
                customItemTotal={customItemTotal}
                existingCustomItemList={customItemList}
                workOrderData={workOrderData}
                onSaveCustomItem={onSaveCustomItem}
              />
              <AdasBox className='mt-4 hidden min-[435px]:flex sticky bottom-3 z-20 items-center justify-between rounded-lg border border-[#D1D5D8] bg-white px-4 py-3 shadow-md'>
                <AdasTypography className='text-[14px] font-semibold uppercase tracking-wide text-[#2C3D4D]'>Total Amount</AdasTypography>
                <AdasTypography className='text-[20px] font-bold text-[#0A1628]'>{currencyFormatter(totalCost)}</AdasTypography>
              </AdasBox>

              {/* Notes */}
              <Note
                existingExternalNote={notes.notes_external}
                existingInternalNote={notes.notes_internal}
                existingClientNote={notes.client_notes}
                submit={(props) => saveNote(props)}
                allowEdit={true}
              />

              {/* Sub-page nav strip — Vehicle Info / Photos / Documents (single line, no wrap) */}
              <AdasBox className='mt-4 flex flex-nowrap items-center gap-2 overflow-x-auto'>
                <button
                  type='button'
                  onClick={redirectVehicleInfo}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Info className='h-4 w-4 shrink-0' />
                  Vehicle Info
                </button>
                <button
                  type='button'
                  onClick={redirectPhotos}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <Camera className='h-4 w-4 shrink-0' />
                  Photos
                </button>
                <button
                  type='button'
                  onClick={redirectDocuments}
                  className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <FileText className='h-4 w-4 shrink-0' />
                  Documents
                </button>
              </AdasBox>
            </AdasBox>
          )}

          {/* Mobile floating bottom action button */}
          <AdasBox className='min-[435px]:hidden fixed bottom-5 left-3 right-3 z-30 flex items-center gap-3'>
            {!isReadOnly && isServiceOnly && !QWIData?.invoiceId && (
              <button
                type='button'
                onClick={ConvertToStandardWoWithConfirmation}
                className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#D1D5D8] bg-white px-4 py-3 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] shadow-lg'
              >
                <RefreshCw className='h-4 w-4' />
                Add Cal.
              </button>
            )}
            {!isReadOnly && (
              <button
                type='button'
                disabled={!isServiceOnly && !calibrationStepsComplete}
                onClick={() => handleAddToInvoice(dataFromInvoiceScreen)}
                className='inline-flex flex-[1.4] items-center justify-center gap-1.5 rounded-full bg-[#0A1628] px-4 py-3 text-[13px] font-bold uppercase tracking-wide text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-40'
              >
                <Plus className='h-4 w-4' />
                {dataFromInvoiceScreen ? 'Update Invoice' : 'Add to Invoice'}
              </button>
            )}
          </AdasBox>
          {/* Spacer below the page content so the floating action bar doesn't cover the last section */}
          <AdasBox className='min-[435px]:hidden h-[88px]' aria-hidden='true' />

          {/* Bottom action footer */}
          <AdasBox className='mt-6 hidden min-[435px]:flex flex-wrap items-center justify-between gap-3 border-t border-[#EBEDF0] pt-4'>
            <button
              type='button'
              onClick={() => history.goBack()}
              className='inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] hover:underline'
            >
              <ArrowLeft className='h-4 w-4' />
              Work Orders
            </button>
            <AdasBox className='flex items-center gap-3'>
              {!isReadOnly && isServiceOnly && !QWIData?.invoiceId && (
                <button
                  type='button'
                  onClick={ConvertToStandardWoWithConfirmation}
                  className='inline-flex items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                >
                  <RefreshCw className='h-4 w-4' />
                  Add Calibrations
                </button>
              )}
              {!isReadOnly && (
                <button
                  type='button'
                  disabled={!isServiceOnly && !calibrationStepsComplete}
                  onClick={() => handleAddToInvoice(dataFromInvoiceScreen)}
                  className='inline-flex items-center gap-2 rounded-lg bg-[#0A1628] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-40'
                >
                  <Plus className='h-4 w-4' />
                  {dataFromInvoiceScreen ? 'Update Invoice' : 'Add to Invoice'}
                </button>
              )}
            </AdasBox>
          </AdasBox>

          {/* Delete WO modal */}
          <BrandedModal
            open={open}
            onClose={handleClose}
            title='Delete Work Order?'
            width='w-[480px]'
            bare
            footer={
              <FormModalFooter
                onCancel={handleClose}
                onConfirm={handleConfirm}
                confirmLabel='Delete'
                confirmDisabled={deleteInput !== 'DELETE'}
                tone='danger'
              />
            }
          >
            <AdasTypography className='mb-4 text-[14px] text-[#2C3D4D]'>
              To confirm that you want to delete this work order, type <span className='font-semibold text-[#0A1628]'>DELETE</span> below.
            </AdasTypography>
            <AdasTextField
              onChange={(e) => setDeleteInput(e.target.value.toUpperCase())}
              name='deleteInput'
              id='deleteInput'
              label='Confirm'
              variant='outlined'
              value={deleteInput}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </BrandedModal>

          {showConfirmModal && (
            <ConfirmModal
              cancelLabel={BUTTONS_DISPLAY_LABEL.CANCEL}
              confirmLabel={BUTTONS_DISPLAY_LABEL.CONFIRM}
              open={showConfirmModal}
              title={GENERIC_MESSAGES.ADD_CALIBRATIONS}
              message={GENERIC_MESSAGES.CLICK_CONFIRM_SERVICE_ONLY_WO_TO_STANDARD_WO_MESSAGE}
              handleClose={() => setShowConfirmModal(false)}
              handleContinue={convertServiceOnlyToStandardWo}
            />
          )}

          <CreateSupportRequestModal open={supportOpen} onClose={() => setSupportOpen(false)} referenceNumber={referenceNumber} />
        </AdasBox>
      )}
    </AdasContainer>
  )
}
